/**
 * RelatorioG2BPCompleto - Relatório Profissional G2 Behavioral Profile (30 páginas)
 * 
 * Estrutura baseada no modelo DISC Profissional:
 * - Capa (1 página)
 * - Índice (1 página)
 * - Introdução ao DISC (4 páginas)
 * - Perfis Natural, Social, Adaptado (3 páginas)
 * - Perfil e Estilo Predominante (2 páginas)
 * - Seu Estilo de Ser - Detalhado (5 páginas)
 * - Características e Intensidades (1 página)
 * - Níveis dos Padrões D/I/S/C (5 páginas)
 * - Desenvolvimento Através do DISC (1 página)
 * - Padrões DISC Descritivo (1 página)
 * - Descrição e Pontos de Atenção (4 páginas)
 * - Conclusão (2 páginas)
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  FileText, 
  Download, 
  Loader2, 
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  AlertTriangle,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

// ==========================================
// CONSTANTES E DADOS DE REFERÊNCIA
// ==========================================

const CORES_DISC = {
  D: { hex: '#dc2626', rgb: [220, 38, 38], nome: 'Vermelho' },
  I: { hex: '#f59e0b', rgb: [245, 158, 11], nome: 'Amarelo' },
  S: { hex: '#22c55e', rgb: [34, 197, 94], nome: 'Verde' },
  C: { hex: '#3b82f6', rgb: [59, 130, 246], nome: 'Azul' }
};

const PERFIS_DISC = {
  D: {
    nome: 'Dominância',
    nomeCompleto: 'DOMINANTE',
    descricao: 'O perfil Dominante é orientado para resultados, desafios e tomada de decisão rápida. Pessoas com alto D são naturalmente assertivas, diretas e focadas em alcançar objetivos. Elas prosperam em ambientes competitivos onde podem exercer controle e tomar decisões importantes.',
    descricaoCompleta: `Você possui um perfil comportamental com alta Dominância, o que significa que sua orientação natural é para resultados e conquistas. Pessoas como você são conhecidas por sua determinação inabalável e capacidade de liderar em situações desafiadoras.

Sua abordagem direta e objetiva pode intimidar alguns, mas é exatamente essa característica que permite que você corte através da indecisão e impulsione projetos adiante. Você não tem medo de tomar decisões difíceis e prefere ação à contemplação prolongada.

No ambiente de trabalho, você provavelmente se destaca em posições que exigem liderança, negociação e resolução de problemas complexos. Sua natureza competitiva o impulsiona a buscar constantemente a excelência e superar obstáculos que outros considerariam intransponíveis.`,
    caracteristicas: ['Determinado', 'Competitivo', 'Direto', 'Decisivo', 'Corajoso', 'Ousado', 'Exigente', 'Enérgico', 'Assertivo', 'Independente', 'Empreendedor', 'Pioneiro'],
    pontosFortes: [
      'Liderança natural e iniciativa excepcional',
      'Foco inabalável em resultados e metas',
      'Tomada de decisão rápida e assertiva',
      'Capacidade de assumir riscos calculados',
      'Resolução eficaz de problemas complexos',
      'Habilidade de trabalhar sob pressão',
      'Visão estratégica e pensamento de longo prazo',
      'Capacidade de motivar outros através do exemplo'
    ],
    pontosAtencao: [
      'Pode parecer autoritário ou impaciente com processos lentos',
      'Tendência a não ouvir completamente os outros antes de decidir',
      'Dificuldade em delegar tarefas que considera importantes',
      'Pode desconsiderar sentimentos alheios em prol de resultados',
      'Necessita desenvolver mais empatia e paciência',
      'Pode ser visto como insensível ou frio',
      'Tendência a dominar conversas e reuniões',
      'Dificuldade em aceitar feedback negativo'
    ],
    motivadores: ['Desafios novos e complexos', 'Autonomia e independência', 'Resultados mensuráveis', 'Controle sobre situações', 'Poder e autoridade', 'Novos projetos inovadores', 'Competição saudável', 'Reconhecimento por conquistas'],
    medos: ['Perda de controle sobre situações importantes', 'Fracasso público ou visível', 'Ser aproveitado ou manipulado', 'Mostrar vulnerabilidade', 'Perder autonomia', 'Ser visto como fraco ou incompetente'],
    comunicacao: 'Para se comunicar efetivamente com pessoas de perfil Dominante, seja direto, objetivo e focado em resultados. Evite rodeios, detalhes desnecessários e conversas longas sem propósito claro. Apresente opções e deixe-os escolher. Respeite seu tempo e vá direto ao ponto principal.',
    ambienteIdeal: 'Ambientes dinâmicos com desafios constantes, autonomia para tomar decisões, oportunidades claras de liderança, foco em resultados mensuráveis, liberdade para inovar e poucos controles burocráticos.',
    contribuicaoEquipe: 'Impulsiona a equipe em direção aos objetivos com determinação. Toma decisões difíceis quando necessário. Assume riscos calculados pelo bem do grupo. Define direção clara e mantém o foco no que importa. Não permite que obstáculos detenham o progresso.',
    descricaoComportamental: 'Pessoas com alto D tendem a agir de forma rápida e decisiva, sempre visando resultados imediatos e tangíveis. Motivam-se naturalmente por desafios e problemas que outros consideram difíceis ou impossíveis. Tomam decisões rapidamente, às vezes antes de ter todas as informações, confiando em sua intuição e experiência. Assumem autoridade naturalmente em grupos e projetos, frequentemente emergindo como líderes mesmo quando não designados formalmente para isso.',
    descricaoAmbiente: 'No ambiente de trabalho, pessoas com alta Dominância desejam poder e autoridade para tomar decisões importantes. Buscam desafios constantes que testem suas habilidades e ampla liberdade de atuação sem microgerenciamento. Preferem respostas diretas e honestas, mesmo que desconfortáveis, e valorizam atividades variadas que evitem monotonia. Rejeitam controles excessivos e processos burocráticos que considerem desnecessários.',
    comoLidar: [
      'Seja direto e vá direto ao ponto',
      'Apresente resultados e benefícios claros',
      'Respeite sua necessidade de controle',
      'Evite detalhes excessivos - foque no essencial',
      'Não leve sua assertividade como algo pessoal',
      'Dê-lhes opções em vez de ordens diretas',
      'Reconheça suas conquistas publicamente'
    ],
    apoioCoach: [
      'Aprender a pesar prós e contras antes de agir impulsivamente',
      'Considerar riscos de forma mais completa e ter mais cautela quando necessário',
      'Basear decisões em fatos e dados, não apenas em intuição',
      'Considerar genuinamente as necessidades e sentimentos dos outros',
      'Desenvolver paciência e empatia como habilidades de liderança',
      'Praticar escuta ativa sem interromper',
      'Aprender a delegar de forma eficaz'
    ],
    frasesRepresentativas: [
      '"Vamos fazer acontecer!"',
      '"Qual é a linha de chegada?"',
      '"Resultados falam mais alto que palavras."',
      '"Se não estamos crescendo, estamos morrendo."',
      '"Tempo é dinheiro - vamos ser objetivos."'
    ],
    carreirasIndicadas: ['CEO/Diretor Executivo', 'Empreendedor', 'Gerente de Vendas', 'Advogado Litigante', 'Consultor Estratégico', 'Negociador', 'Líder de Projetos', 'Militar/Oficial'],
    estiloLideranca: 'Liderança diretiva e orientada a resultados. Toma decisões rapidamente e espera que a equipe execute com eficiência. Define metas ambiciosas e cobra performance. Pode ser inspirador em momentos de crise.',
    sobPressao: 'Sob pressão, pessoas com alto D podem se tornar ainda mais assertivas e controladoras. Podem parecer impacientes, ditatoriais ou insensíveis. A tendência é tomar controle total da situação e desconsiderar opiniões divergentes.'
  },
  I: {
    nome: 'Influência',
    nomeCompleto: 'INFLUENTE',
    descricao: 'O perfil Influente é comunicativo, entusiasta e focado em relacionamentos. Pessoas com alto I são naturalmente sociáveis, otimistas e têm facilidade para criar conexões. Elas energizam ambientes e motivam pessoas através de seu entusiasmo contagiante.',
    descricaoCompleta: `Você possui um perfil comportamental com alta Influência, o que significa que sua orientação natural é para pessoas e relacionamentos. Você tem um dom especial para se conectar com outros, criar ambientes positivos e inspirar pessoas ao seu redor.

Sua energia contagiante e otimismo natural fazem de você uma presença marcante em qualquer grupo. Você provavelmente é a pessoa que inicia conversas, que conhece todos pelo nome, e que consegue encontrar o lado positivo mesmo em situações difíceis.

No ambiente de trabalho, você se destaca em funções que envolvem comunicação, vendas, marketing, treinamento ou qualquer área que exija habilidades interpessoais. Sua capacidade de persuadir e entusiasmar outros é um ativo valioso em equipes e projetos.`,
    caracteristicas: ['Comunicativo', 'Otimista', 'Persuasivo', 'Sociável', 'Entusiasta', 'Inspirador', 'Empático', 'Convincente', 'Carismático', 'Expressivo', 'Animado', 'Confiante'],
    pontosFortes: [
      'Excelente capacidade de networking e criação de conexões',
      'Habilidade natural de motivar e inspirar equipes',
      'Criatividade abundante e pensamento inovador',
      'Persuasão e comunicação eficaz e envolvente',
      'Capacidade de criar ambientes positivos e energizados',
      'Facilidade para falar em público e apresentações',
      'Habilidade de resolver conflitos através do diálogo',
      'Otimismo que mantém equipes motivadas em momentos difíceis'
    ],
    pontosAtencao: [
      'Organização pessoal e gestão eficaz do tempo',
      'Atenção aos detalhes e tarefas rotineiras',
      'Disciplina para seguir processos estabelecidos',
      'Manter foco em tarefas longas e repetitivas',
      'Objetividade em decisões que envolvem pessoas queridas',
      'Tendência a se comprometer demais e não cumprir',
      'Dificuldade em dar feedback negativo necessário',
      'Pode priorizar popularidade sobre resultados'
    ],
    motivadores: ['Reconhecimento público e aplausos', 'Interação social constante', 'Liberdade de expressão', 'Novidades e variedade', 'Popularidade e aceitação', 'Aprovação dos outros', 'Ambiente divertido', 'Oportunidades de networking'],
    medos: ['Rejeição social e desaprovação', 'Perda de influência e relevância', 'Ser ignorado ou esquecido', 'Ambientes hostis ou frios', 'Rotina monótona', 'Críticas públicas'],
    comunicacao: 'Para se comunicar com pessoas de perfil Influente, seja amigável, aberto e entusiasmado. Permita que eles se expressem e conte histórias. Reconheça suas contribuições publicamente. Use humor e mantenha a conversa leve quando possível. Evite ser excessivamente formal ou crítico.',
    ambienteIdeal: 'Ambientes sociais e colaborativos, com reconhecimento público frequente, liberdade para expressar ideias criativas, oportunidades de interação com pessoas diversas e ausência de rotinas rígidas ou tarefas solitárias.',
    contribuicaoEquipe: 'Motiva e inspira colegas com seu entusiasmo contagiante. Cria ambiente positivo e acolhedor. Facilita comunicação entre diferentes grupos e níveis. Promove colaboração e espírito de equipe. É a "cola social" que mantém o grupo unido.',
    descricaoComportamental: 'Pessoas com alto I contatam pessoas naturalmente e com facilidade, causando boa impressão desde o primeiro momento. São eloquentes e motivadores, capazes de vender ideias e criar consenso. Participam ativamente de grupos e atividades sociais, e mantêm otimismo mesmo em situações adversas. Preferem trabalhar com pessoas do que sozinhas.',
    descricaoAmbiente: 'No ambiente de trabalho, pessoas com alta Influência desejam popularidade e reconhecimento público por suas contribuições. Buscam liberdade de expressão para compartilhar ideias e opiniões. Preferem atividades em grupo e relacionamentos colaborativos e democráticos. Valorizam ambientes onde possam socializar e construir conexões.',
    comoLidar: [
      'Seja amigável e mostre interesse genuíno',
      'Deixe-os falar e expressar suas ideias',
      'Reconheça suas contribuições publicamente',
      'Use humor e mantenha o clima leve',
      'Não seja excessivamente crítico ou formal',
      'Envolva-os em atividades de grupo',
      'Dê feedback de forma construtiva e positiva'
    ],
    apoioCoach: [
      'Desenvolver foco e disciplina em tarefas rotineiras',
      'Aprender a dizer "não" quando necessário',
      'Basear-se mais em fatos e dados concretos',
      'Ser mais direto quando a situação exigir',
      'Desenvolver abordagens sistemáticas para projetos',
      'Fazer acompanhamento pessoal de compromissos assumidos',
      'Equilibrar socialização com produtividade'
    ],
    frasesRepresentativas: [
      '"Vamos fazer isso juntos!"',
      '"Que ideia fantástica - adoro!"',
      '"Conheci uma pessoa incrível outro dia..."',
      '"Vamos celebrar essa conquista!"',
      '"Eu tenho uma ideia genial!"'
    ],
    carreirasIndicadas: ['Vendas e Desenvolvimento de Negócios', 'Marketing e Publicidade', 'Relações Públicas', 'Treinamento e Desenvolvimento', 'RH - Recrutamento', 'Apresentador/Comunicador', 'Coach/Mentor', 'Eventos e Entretenimento'],
    estiloLideranca: 'Liderança inspiradora e participativa. Motiva através do exemplo e entusiasmo. Cria ambiente colaborativo e incentiva contribuições de todos. Prefere consenso a imposição.',
    sobPressao: 'Sob pressão, pessoas com alto I podem se tornar desorganizadas e dispersas. Podem buscar aprovação excessivamente, evitar conflitos necessários ou fazer promessas que não conseguem cumprir. A tendência é buscar apoio emocional dos outros.'
  },
  S: {
    nome: 'Estabilidade',
    nomeCompleto: 'ESTÁVEL',
    descricao: 'O perfil Estável é paciente, leal e focado em harmonia e cooperação. Pessoas com alto S são naturalmente calmas, confiáveis e dedicadas. Elas criam ambientes seguros e previsíveis, valorizando relacionamentos duradouros e estabilidade.',
    descricaoCompleta: `Você possui um perfil comportamental com alta Estabilidade, o que significa que sua orientação natural é para harmonia, cooperação e relacionamentos duradouros. Você é a rocha sobre a qual outros podem se apoiar - consistente, confiável e sempre presente.

Sua natureza paciente e acolhedora faz de você um excelente ouvinte e conselheiro. Você cria ambientes onde as pessoas se sentem seguras para serem autênticas. Sua lealdade aos colegas e à organização é inabalável.

No ambiente de trabalho, você se destaca em funções que exigem consistência, suporte a outros e manutenção de processos. Sua capacidade de mediar conflitos e manter a calma em situações tensas é extremamente valiosa para qualquer equipe.`,
    caracteristicas: ['Paciente', 'Leal', 'Cooperativo', 'Tranquilo', 'Confiável', 'Consistente', 'Bom ouvinte', 'Ponderado', 'Dedicado', 'Sincero', 'Diplomático', 'Persistente'],
    pontosFortes: [
      'Manutenção excepcional da harmonia na equipe',
      'Alta confiabilidade e consistência nas entregas',
      'Excelente capacidade de suporte aos colegas',
      'Persistência e dedicação a longo prazo',
      'Habilidade natural de mediação de conflitos',
      'Capacidade de escuta ativa e empática',
      'Lealdade inabalável à equipe e organização',
      'Criação de ambientes seguros e acolhedores'
    ],
    pontosAtencao: [
      'Dificuldade de adaptação a mudanças rápidas',
      'Assertividade e posicionamento claro podem ser desafiadores',
      'Hesitação em tomar iniciativa proativamente',
      'Desafio ao lidar com múltiplas prioridades simultâneas',
      'Dificuldade em expressar discordância abertamente',
      'Pode evitar conflitos necessários',
      'Tendência a aceitar mais trabalho do que consegue fazer',
      'Pode ser visto como passivo ou indeciso'
    ],
    motivadores: ['Estabilidade e previsibilidade', 'Segurança no emprego e relacionamentos', 'Relacionamentos profundos e duradouros', 'Reconhecimento sincero e pessoal', 'Rotinas estabelecidas', 'Harmonia no ambiente', 'Tempo para processar mudanças', 'Trabalho em equipe colaborativo'],
    medos: ['Mudanças bruscas e inesperadas', 'Instabilidade no trabalho ou relacionamentos', 'Conflitos interpessoais', 'Pressão excessiva ou prazos apertados', 'Ambiente caótico ou imprevisível', 'Perda de relacionamentos importantes'],
    comunicacao: 'Para se comunicar com pessoas de perfil Estável, seja paciente e mostre interesse genuíno em seus pensamentos. Dê tempo para que processem mudanças e novas informações. Seja sincero e mantenha consistência em suas palavras e ações. Evite pressão excessiva ou ultimatos.',
    ambienteIdeal: 'Ambientes estáveis e previsíveis, com relacionamentos de confiança estabelecidos, reconhecimento sincero pelo trabalho realizado, tempo adequado para mudanças e clara definição de expectativas e responsabilidades.',
    contribuicaoEquipe: 'Mantém a harmonia e coesão da equipe. Oferece suporte consistente e confiável aos colegas. Medeia conflitos com diplomacia. Garante que processos sejam seguidos. É a memória institucional do grupo.',
    descricaoComportamental: 'Pessoas com alto S demonstram consistência notável em seu comportamento e entregas. São pacientes com processos e pessoas, raramente perdendo a calma. Desenvolvem habilidades especializadas ao longo do tempo e são extremamente leais a pessoas e organizações. São excelentes ouvintes que buscam genuinamente entender antes de responder.',
    descricaoAmbiente: 'No ambiente de trabalho, pessoas com alta Estabilidade desejam manter o status quo quando as coisas funcionam bem. Preferem rotinas previsíveis e procedimentos padronizados. Valorizam crédito sincero pelo trabalho realizado e apreciação genuína. Buscam interferência mínima em seus métodos estabelecidos.',
    comoLidar: [
      'Seja paciente e dê tempo para respostas',
      'Mostre interesse genuíno em seu bem-estar',
      'Introduza mudanças gradualmente',
      'Seja consistente em palavras e ações',
      'Reconheça seu trabalho de forma sincera',
      'Evite pressão excessiva ou confrontos',
      'Valorize sua lealdade e dedicação'
    ],
    apoioCoach: [
      'Desenvolver capacidade de reagir positivamente a mudanças',
      'Praticar versatilidade em diferentes tipos de tarefas',
      'Aprender a se promover adequadamente',
      'Desenvolver conforto em ambientes imprevisíveis',
      'Aprender a priorizar e ser mais flexível',
      'Praticar assertividade construtiva',
      'Desenvolver confiança para tomar iniciativa'
    ],
    frasesRepresentativas: [
      '"Como posso ajudar?"',
      '"Vamos pensar bem antes de decidir."',
      '"Prefiro fazer bem feito do que rápido."',
      '"A equipe vem primeiro."',
      '"Sempre fiz assim e funciona."'
    ],
    carreirasIndicadas: ['Recursos Humanos', 'Atendimento ao Cliente', 'Enfermagem/Saúde', 'Administração', 'Suporte Técnico', 'Assistente Executivo', 'Professor', 'Conselheiro/Terapeuta'],
    estiloLideranca: 'Liderança servidora e de apoio. Lidera pelo exemplo de dedicação e consistência. Cria ambiente seguro para a equipe. Prefere consenso e evita impor decisões.',
    sobPressao: 'Sob pressão, pessoas com alto S podem se tornar passivas ou evasivas. Podem concordar superficialmente para evitar conflitos ou ter dificuldade em tomar decisões rápidas. A tendência é buscar apoio e orientação de outros.'
  },
  C: {
    nome: 'Conformidade',
    nomeCompleto: 'CAUTELOSO',
    descricao: 'O perfil Cauteloso é analítico, detalhista e focado em qualidade e precisão. Pessoas com alto C são naturalmente sistemáticas, organizadas e comprometidas com a excelência. Elas garantem que as coisas sejam feitas corretamente.',
    descricaoCompleta: `Você possui um perfil comportamental com alta Conformidade, o que significa que sua orientação natural é para qualidade, precisão e excelência. Você é a pessoa que garante que os detalhes sejam cuidados e que os padrões sejam mantidos.

Sua mente analítica e sistemática permite que você identifique problemas que outros não percebem. Você não aceita "mais ou menos" - para você, ou está correto ou não está. Essa busca pela perfeição eleva a qualidade de tudo que você toca.

No ambiente de trabalho, você se destaca em funções que exigem análise detalhada, controle de qualidade, planejamento ou qualquer área onde precisão e atenção aos detalhes sejam cruciais. Sua capacidade de seguir processos e identificar falhas é inestimável.`,
    caracteristicas: ['Analítico', 'Preciso', 'Organizado', 'Sistemático', 'Detalhista', 'Perfeccionista', 'Diplomático', 'Prudente', 'Lógico', 'Metódico', 'Crítico', 'Reservado'],
    pontosFortes: [
      'Atenção excepcional aos detalhes importantes',
      'Capacidade de análise crítica e lógica profunda',
      'Comprometimento com altos padrões de qualidade',
      'Organização impecável e planejamento detalhado',
      'Precisão e consistência em todas as entregas',
      'Habilidade de identificar riscos e problemas potenciais',
      'Tomada de decisão baseada em fatos e dados',
      'Expertise técnica profunda em suas áreas de atuação'
    ],
    pontosAtencao: [
      'Paralisia por análise excessiva pode atrasar decisões',
      'Flexibilidade e adaptação a situações imperfeitas',
      'Velocidade na tomada de decisão quando necessário',
      'Delegação de tarefas que considera importantes',
      'Aceitar imperfeições necessárias em prol de prazos',
      'Pode ser visto como crítico demais ou pessimista',
      'Dificuldade em trabalhar com informações incompletas',
      'Pode se isolar socialmente em busca de concentração'
    ],
    motivadores: ['Qualidade e excelência reconhecidas', 'Precisão e acurácia em tudo', 'Reconhecimento técnico e expertise', 'Processos claros e bem definidos', 'Tempo para análise adequada', 'Ambiente organizado', 'Autonomia técnica', 'Desafios intelectuais'],
    medos: ['Cometer erros ou ser responsável por falhas', 'Críticas ao seu trabalho ou competência', 'Caos, desordem e falta de padrões', 'Decisões precipitadas sem análise', 'Ser visto como incompetente', 'Ambiguidade e incerteza'],
    comunicacao: 'Para se comunicar com pessoas de perfil Cauteloso, seja preciso e baseado em fatos verificáveis. Forneça dados, evidências e tempo adequado para análise. Evite generalizações vagas ou decisões impulsivas. Apresente informações de forma organizada e lógica.',
    ambienteIdeal: 'Ambientes organizados e estruturados, com expectativas claras e bem definidas, foco em qualidade sobre quantidade, reconhecimento da expertise técnica e tempo adequado para análise e planejamento.',
    contribuicaoEquipe: 'Garante qualidade e precisão nas entregas. Identifica erros e riscos antes que causem problemas. Mantém padrões elevados para toda a equipe. Fornece análises detalhadas para tomada de decisão. Documenta processos e conhecimento.',
    descricaoComportamental: 'Pessoas com alto C aderem estritamente a diretrizes e padrões de qualidade. São extremamente detalhistas e analíticas, examinando todas as facetas de um problema. Comportam-se de forma diplomática e sistemática, evitando conflitos desnecessários. Buscam precisão e qualidade em tudo que fazem.',
    descricaoAmbiente: 'No ambiente de trabalho, pessoas com alta Conformidade desejam expectativas claras e bem definidas. Preferem atmosfera profissional e organizada. Valorizam oportunidades de demonstrar expertise técnica e controle sobre fatores que afetam seu desempenho. Buscam reconhecimento pela qualidade de seu trabalho.',
    comoLidar: [
      'Forneça informações detalhadas e precisas',
      'Dê tempo para análise antes de cobrar decisões',
      'Respeite sua necessidade de qualidade',
      'Apresente dados e fatos, não opiniões',
      'Seja paciente com suas perguntas detalhadas',
      'Reconheça sua expertise técnica',
      'Evite pressão excessiva ou prazos impossíveis'
    ],
    apoioCoach: [
      'Desenvolver capacidade de tomar decisões com informações incompletas',
      'Aprender a delegar mais efetivamente',
      'Equilibrar perfeição com praticidade',
      'Usar regras como diretrizes, não absolutas',
      'Desenvolver conforto em apresentar posições mesmo quando desagradáveis',
      'Incentivar o trabalho em equipe e colaboração',
      'Praticar flexibilidade quando necessário'
    ],
    frasesRepresentativas: [
      '"Deixe-me analisar os dados primeiro."',
      '"Precisamos de mais informações."',
      '"Existe uma forma certa de fazer isso."',
      '"Qual é a base para essa conclusão?"',
      '"Qualidade não é negociável."'
    ],
    carreirasIndicadas: ['Analista de Dados/BI', 'Contador/Auditor', 'Engenheiro', 'Cientista/Pesquisador', 'Desenvolvedor de Software', 'Controle de Qualidade', 'Compliance/Regulatório', 'Arquiteto/Planejador'],
    estiloLideranca: 'Liderança técnica e baseada em expertise. Lidera através do conhecimento e exemplo de qualidade. Define padrões elevados e espera que sejam seguidos. Prefere análise cuidadosa antes de ação.',
    sobPressao: 'Sob pressão, pessoas com alto C podem se tornar excessivamente críticas ou retraídas. Podem insistir em análise quando ação é necessária, ou se isolar para "pensar". A tendência é buscar mais dados e evitar decisões que possam estar erradas.'
  }
};

// 15 Estilos comportamentais combinados com descrições detalhadas
const ESTILOS_COMBINADOS = {
  'D': { 
    nome: 'Dominante Puro', 
    descricao: 'Focado intensamente em resultados e controle. Direto, assertivo e orientado para ação imediata.',
    descricaoCompleta: 'Você apresenta um perfil de Dominância pura, o que significa que sua orientação primária é para resultados, desafios e controle. Você é naturalmente assertivo, direto e não tem medo de tomar decisões difíceis. Sua energia é canalizada para alcançar objetivos e superar obstáculos. Em equipes, você tende a emergir como líder, definindo direção e mantendo todos focados no que importa.',
    pontosChave: ['Liderança natural', 'Foco em resultados', 'Decisões rápidas', 'Independência'],
    desafios: ['Pode parecer impaciente', 'Dificuldade em ouvir outros', 'Tendência a controlar demais']
  },
  'I': { 
    nome: 'Influente Puro', 
    descricao: 'Focado em pessoas e comunicação. Entusiasta, persuasivo e criador de conexões.',
    descricaoCompleta: 'Você apresenta um perfil de Influência pura, o que significa que sua orientação primária é para pessoas, relacionamentos e comunicação. Você é naturalmente sociável, otimista e tem uma capacidade única de criar conexões e energizar ambientes. Seu entusiasmo é contagiante e você prospera em situações que envolvem interação humana.',
    pontosChave: ['Comunicação excepcional', 'Entusiasmo contagiante', 'Networking natural', 'Criatividade'],
    desafios: ['Pode perder foco em detalhes', 'Dificuldade com rotinas', 'Tendência a se comprometer demais']
  },
  'S': { 
    nome: 'Estável Puro', 
    descricao: 'Focado em harmonia e consistência. Paciente, leal e extremamente confiável.',
    descricaoCompleta: 'Você apresenta um perfil de Estabilidade pura, o que significa que sua orientação primária é para harmonia, cooperação e relacionamentos duradouros. Você é a rocha sobre a qual outros podem se apoiar - consistente, confiável e sempre disposto a ajudar. Sua paciência e lealdade são qualidades raras e valiosas em qualquer ambiente.',
    pontosChave: ['Lealdade inabalável', 'Consistência exemplar', 'Excelente ouvinte', 'Mediador natural'],
    desafios: ['Dificuldade com mudanças rápidas', 'Pode evitar confrontos necessários', 'Hesitação em tomar iniciativa']
  },
  'C': { 
    nome: 'Cauteloso Puro', 
    descricao: 'Focado em qualidade e precisão. Analítico, sistemático e comprometido com a excelência.',
    descricaoCompleta: 'Você apresenta um perfil de Conformidade pura, o que significa que sua orientação primária é para qualidade, precisão e excelência. Você é naturalmente analítico, sistemático e não aceita "mais ou menos". Sua atenção aos detalhes e busca pela perfeição garantem que tudo que você toca atinja os mais altos padrões.',
    pontosChave: ['Atenção aos detalhes', 'Análise profunda', 'Padrões elevados', 'Organização impecável'],
    desafios: ['Pode ser visto como perfeccionista demais', 'Dificuldade em decidir rapidamente', 'Tendência ao isolamento']
  },
  'DI': { 
    nome: 'Inspirador', 
    descricao: 'Combina assertividade com entusiasmo. Lidera motivando pessoas através de energia, carisma e visão.',
    descricaoCompleta: 'Você apresenta um estilo Inspirador (DI), combinando a assertividade e foco em resultados da Dominância com o entusiasmo e habilidade social da Influência. Você é um líder carismático que não apenas define a direção, mas consegue energizar e motivar pessoas a segui-lo. Sua combinação de determinação e carisma o torna especialmente eficaz em papéis que exigem liderar mudanças e conquistar apoio para novas iniciativas.',
    pontosChave: ['Liderança carismática', 'Visão inspiradora', 'Energia contagiante', 'Persuasão assertiva'],
    desafios: ['Pode parecer autoritário', 'Impaciência com detalhes', 'Pode dominar conversas']
  },
  'DC': { 
    nome: 'Realizador', 
    descricao: 'Une determinação com precisão. Focado em resultados de alta qualidade com excelência técnica.',
    descricaoCompleta: 'Você apresenta um estilo Realizador (DC), combinando a determinação e foco em resultados da Dominância com a precisão e busca por qualidade da Conformidade. Você não apenas quer resultados - quer resultados excelentes. Esta combinação o torna especialmente eficaz em projetos complexos que exigem tanto liderança quanto atenção aos detalhes técnicos.',
    pontosChave: ['Excelência nas entregas', 'Padrões elevados', 'Determinação focada', 'Competência técnica'],
    desafios: ['Exigente consigo e com outros', 'Pode ser visto como frio', 'Dificuldade em aceitar trabalho medíocre']
  },
  'DS': { 
    nome: 'Empreendedor', 
    descricao: 'Combina iniciativa com persistência. Busca resultados de forma consistente e determinada.',
    descricaoCompleta: 'Você apresenta um estilo Empreendedor (DS), combinando a iniciativa e assertividade da Dominância com a persistência e consistência da Estabilidade. Você define objetivos ambiciosos e persegue-os com determinação constante. Esta combinação o torna especialmente eficaz em projetos de longo prazo que exigem tanto visão quanto persistência.',
    pontosChave: ['Persistência determinada', 'Visão de longo prazo', 'Consistência nos objetivos', 'Resiliência'],
    desafios: ['Pode resistir a mudanças de direção', 'Dificuldade em delegar', 'Pode ser teimoso']
  },
  'ID': { 
    nome: 'Promotor', 
    descricao: 'Comunicativo e orientado para ação. Persuasivo, direto e motivador nato.',
    descricaoCompleta: 'Você apresenta um estilo Promotor (ID), combinando as habilidades sociais e entusiasmo da Influência com a assertividade e foco em resultados da Dominância. Você é um comunicador persuasivo que também sabe ser direto e orientado para ação. Esta combinação o torna especialmente eficaz em vendas, marketing e qualquer papel que exija convencer e mobilizar pessoas.',
    pontosChave: ['Persuasão poderosa', 'Energia para ação', 'Comunicação direta', 'Motivação de equipes'],
    desafios: ['Pode ser impulsivo', 'Dificuldade com detalhes', 'Pode pressionar demais']
  },
  'IS': { 
    nome: 'Conselheiro', 
    descricao: 'Amigável e solidário. Excelente para relacionamentos profundos, suporte e aconselhamento.',
    descricaoCompleta: 'Você apresenta um estilo Conselheiro (IS), combinando as habilidades sociais e otimismo da Influência com a paciência e lealdade da Estabilidade. Você é uma presença acolhedora que cria relacionamentos profundos e duradouros. Esta combinação o torna especialmente eficaz em papéis de suporte, aconselhamento e desenvolvimento de pessoas.',
    pontosChave: ['Empatia profunda', 'Relacionamentos duradouros', 'Suporte genuíno', 'Ambiente acolhedor'],
    desafios: ['Pode evitar confrontos', 'Dificuldade em dar feedback negativo', 'Pode priorizar harmonia sobre resultados']
  },
  'IC': { 
    nome: 'Comunicador Analítico', 
    descricao: 'Criativo com atenção aos detalhes. Persuasivo e preciso em suas comunicações.',
    descricaoCompleta: 'Você apresenta um estilo Comunicador Analítico (IC), combinando as habilidades sociais e criatividade da Influência com a precisão e análise da Conformidade. Você comunica de forma envolvente, mas sempre embasado em fatos e dados. Esta combinação o torna especialmente eficaz em apresentações técnicas, treinamentos e comunicação de informações complexas.',
    pontosChave: ['Comunicação precisa', 'Criatividade fundamentada', 'Apresentações impactantes', 'Equilíbrio social-técnico'],
    desafios: ['Pode analisar demais antes de agir', 'Dificuldade em situações caóticas', 'Pode ser indeciso']
  },
  'SD': { 
    nome: 'Especialista', 
    descricao: 'Paciente mas determinado. Consistente na busca de objetivos de longo prazo.',
    descricaoCompleta: 'Você apresenta um estilo Especialista (SD), combinando a paciência e consistência da Estabilidade com a determinação e foco em resultados da Dominância. Você desenvolve expertise profunda ao longo do tempo e persegue objetivos com persistência inabalável. Esta combinação o torna especialmente eficaz em projetos especializados que exigem conhecimento profundo e foco sustentado.',
    pontosChave: ['Expertise profunda', 'Persistência focada', 'Confiabilidade', 'Determinação paciente'],
    desafios: ['Pode resistir a novas abordagens', 'Dificuldade com mudanças rápidas', 'Pode ser territorial']
  },
  'SI': { 
    nome: 'Facilitador', 
    descricao: 'Harmonizador natural. Cria ambientes positivos, colaborativos e acolhedores.',
    descricaoCompleta: 'Você apresenta um estilo Facilitador (SI), combinando a paciência e lealdade da Estabilidade com as habilidades sociais e otimismo da Influência. Você é um harmonizador natural que cria ambientes onde as pessoas se sentem valorizadas e incluídas. Esta combinação o torna especialmente eficaz em papéis que exigem construção de equipes e mediação.',
    pontosChave: ['Harmonia natural', 'Inclusão de todos', 'Ambiente positivo', 'Colaboração efetiva'],
    desafios: ['Pode evitar conflitos necessários', 'Dificuldade em tomar posição', 'Pode ser visto como passivo']
  },
  'SC': { 
    nome: 'Analista Estável', 
    descricao: 'Metódico e confiável. Focado em qualidade com consistência admirável.',
    descricaoCompleta: 'Você apresenta um estilo Analista Estável (SC), combinando a paciência e consistência da Estabilidade com a precisão e análise da Conformidade. Você é metódico, confiável e comprometido com a qualidade. Esta combinação o torna especialmente eficaz em papéis técnicos que exigem atenção aos detalhes e entregas consistentes.',
    pontosChave: ['Qualidade consistente', 'Análise cuidadosa', 'Confiabilidade técnica', 'Processos sólidos'],
    desafios: ['Pode ser lento em decidir', 'Dificuldade com pressão', 'Resistência a mudanças']
  },
  'CD': { 
    nome: 'Perfeccionista Assertivo', 
    descricao: 'Exigente e direto. Busca excelência com determinação e não aceita menos que o melhor.',
    descricaoCompleta: 'Você apresenta um estilo Perfeccionista Assertivo (CD), combinando a precisão e busca por qualidade da Conformidade com a assertividade e foco em resultados da Dominância. Você exige excelência de si mesmo e dos outros, e não tem medo de cobrar padrões elevados. Esta combinação o torna especialmente eficaz em papéis de controle de qualidade e liderança técnica.',
    pontosChave: ['Padrões elevados', 'Cobrança assertiva', 'Excelência técnica', 'Resultados de qualidade'],
    desafios: ['Pode ser visto como crítico demais', 'Dificuldade em aceitar imperfeições', 'Pode intimidar outros']
  },
  'CI': { 
    nome: 'Avaliador', 
    descricao: 'Analítico mas comunicativo. Combina precisão técnica com habilidade social.',
    descricaoCompleta: 'Você apresenta um estilo Avaliador (CI), combinando a precisão e análise da Conformidade com as habilidades sociais e comunicação da Influência. Você consegue analisar profundamente e também comunicar suas descobertas de forma envolvente. Esta combinação o torna especialmente eficaz em consultoria, pesquisa e papéis que exigem traduzir complexidade técnica para públicos diversos.',
    pontosChave: ['Análise comunicável', 'Precisão social', 'Consultoria eficaz', 'Tradução técnica'],
    desafios: ['Pode oscilar entre análise e ação', 'Dificuldade em ambientes muito informais ou muito técnicos', 'Pode ser indeciso']
  },
  'CS': { 
    nome: 'Especialista Técnico', 
    descricao: 'Preciso e paciente. Desenvolve expertise profunda com consistência e dedicação.',
    descricaoCompleta: 'Você apresenta um estilo Especialista Técnico (CS), combinando a precisão e análise da Conformidade com a paciência e consistência da Estabilidade. Você desenvolve conhecimento profundo ao longo do tempo e mantém padrões elevados de forma consistente. Esta combinação o torna especialmente eficaz em papéis técnicos especializados que exigem expertise e confiabilidade.',
    pontosChave: ['Expertise técnica', 'Consistência de qualidade', 'Paciência analítica', 'Confiabilidade profunda'],
    desafios: ['Pode ser lento em decidir', 'Dificuldade com pressão ou caos', 'Tendência ao isolamento']
  }
};

// Tabela de características e intensidades
const CARACTERISTICAS_INTENSIDADE = {
  D: {
    alto: ['Egocêntrico', 'Direto', 'Ousado', 'Dominador', 'Exigente', 'Enérgico', 'Disposto a correr riscos', 'Aventureiro', 'Decidido', 'Inquisitivo', 'Autossuficiente'],
    baixo: ['Conservador', 'Cooperativo', 'Cauteloso', 'Agradável', 'Modesto', 'Pacífico', 'Discreto']
  },
  I: {
    alto: ['Entusiasta', 'Social', 'Persuasivo', 'Impulsivo', 'Emocional', 'Confiante', 'Influente', 'Agradável', 'Sociável', 'Generoso', 'Encantador'],
    baixo: ['Recatado', 'Desconfiado', 'Pessimista', 'Distante', 'Retraído', 'Inibido', 'Contido', 'Seletivo']
  },
  S: {
    alto: ['Passivo', 'Paciente', 'Leal', 'Previsível', 'Pessoa de Equipe', 'Calmo', 'Estável', 'Relaxado', 'Sincero', 'Modesto', 'Tranquilo'],
    baixo: ['Alerta', 'Inquieto', 'Impaciente', 'Impulsivo', 'Espontâneo', 'Agitado', 'Tenso', 'Crítico']
  },
  C: {
    alto: ['Perfeccionista', 'Preciso', 'Descobridor de Fatos', 'Diplomático', 'Sistemático', 'Convencional', 'Cuidadoso', 'Contido', 'Altos Padrões', 'Analítico', 'Prudente'],
    baixo: ['Independente', 'Teimoso', 'Obstinado', 'Descuidado', 'Opinativo', 'Desorganizado', 'Flexível']
  }
};

// Grau de conforto entre perfis
const GRAU_CONFORTO = {
  'D-D': { grau: 2, descricao: 'Competição intensa. Ambos querem liderar.' },
  'D-I': { grau: 4, descricao: 'Boa complementaridade. D lidera, I comunica.' },
  'D-S': { grau: 3, descricao: 'S fornece estabilidade que D precisa.' },
  'D-C': { grau: 3, descricao: 'C fornece análise, D fornece ação.' },
  'I-I': { grau: 4, descricao: 'Muita energia social, pode faltar foco.' },
  'I-S': { grau: 5, descricao: 'Excelente harmonia e comunicação.' },
  'I-C': { grau: 2, descricao: 'I é rápido, C é metódico. Tensão possível.' },
  'S-S': { grau: 5, descricao: 'Harmonia total, mas pode faltar iniciativa.' },
  'S-C': { grau: 4, descricao: 'Ambos são cautelosos e metódicos.' },
  'C-C': { grau: 3, descricao: 'Muito análise, pode faltar ação.' }
};

// ==========================================
// FUNÇÃO PRINCIPAL DE GERAÇÃO DO PDF
// ==========================================

const generateRelatorioCompleto = async (data, psicologo, onProgress) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  let currentPage = 1;
  const totalPages = 30;
  
  // Detectar versão e extrair dados
  const isV2 = data.versao === '2.0';
  const percentuaisSintese = isV2 ? data.percentuaisSintese : (data.percentages || { D: 25, I: 25, S: 25, C: 25 });
  const percentuaisMais = isV2 ? data.percentuaisMais : percentuaisSintese;
  const percentuaisMenos = isV2 ? data.percentuaisMenos : percentuaisSintese;
  
  // Identificar perfil dominante e estilo
  const sorted = Object.entries(percentuaisSintese).sort(([,a], [,b]) => b - a);
  const dominant = sorted[0][0];
  const secondary = sorted[1][0];
  const estilo = sorted[1][1] >= sorted[0][1] * 0.7 ? dominant + secondary : dominant;
  const estiloInfo = ESTILOS_COMBINADOS[estilo] || ESTILOS_COMBINADOS[dominant];
  const perfilDominante = PERFIS_DISC[dominant];
  
  // Helper functions
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
  };
  
  const addHeader = (title, showProfile = true) => {
    pdf.setFillColor(79, 70, 229);
    pdf.rect(0, 0, pageWidth, 18, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('G2 Behavioral Profile', margin, 12);
    if (showProfile) {
      pdf.text(data.nome, pageWidth - margin, 12, { align: 'right' });
    }
    return 28;
  };
  
  const addFooter = (pageNum) => {
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`G2eGente - Plataforma de Recrutamento Ético`, margin, pageHeight - 10);
    pdf.text(`Página ${pageNum} de ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    
    // Indicador DISC no rodapé
    const indicatorY = pageHeight - 15;
    const indicatorX = pageWidth / 2 - 30;
    ['D', 'I', 'S', 'C'].forEach((t, i) => {
      const rgb = CORES_DISC[t].rgb;
      pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
      pdf.circle(indicatorX + (i * 15), indicatorY, 3, 'F');
      pdf.setFontSize(6);
      pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
      pdf.text(`${percentuaisSintese[t]}`, indicatorX + (i * 15), indicatorY + 6, { align: 'center' });
    });
  };
  
  const addNewPage = () => {
    pdf.addPage();
    currentPage++;
    if (onProgress) onProgress(Math.round((currentPage / totalPages) * 100));
  };
  
  const drawBarChart = (x, y, width, height, percentages, showLabels = true, title = '') => {
    const barWidth = (width - 30) / 4;
    const startX = x + 15;
    
    if (title) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(79, 70, 229);
      pdf.text(title, x + width/2, y - 5, { align: 'center' });
    }
    
    ['D', 'I', 'S', 'C'].forEach((trait, index) => {
      const barX = startX + (index * (barWidth + 5));
      const score = percentages[trait] || 0;
      const barH = (score / 100) * height;
      const rgb = CORES_DISC[trait].rgb;
      
      // Fundo
      pdf.setFillColor(241, 245, 249);
      pdf.rect(barX, y, barWidth, height, 'F');
      
      // Barra
      pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
      pdf.rect(barX, y + (height - barH), barWidth, barH, 'F');
      
      if (showLabels) {
        // Label
        pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(trait, barX + barWidth/2, y + height + 8, { align: 'center' });
        
        // Percentual
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(8);
        pdf.text(`${score}%`, barX + barWidth/2, y - 2, { align: 'center' });
      }
    });
  };
  
  const drawDISCCircle = (centerX, centerY, radius) => {
    // Desenhar círculo DISC com quadrantes
    const quadrants = [
      { trait: 'D', angle: -45, label: 'DOMINÂNCIA' },
      { trait: 'I', angle: 45, label: 'INFLUÊNCIA' },
      { trait: 'S', angle: 135, label: 'ESTABILIDADE' },
      { trait: 'C', angle: -135, label: 'CONFORMIDADE' }
    ];
    
    quadrants.forEach((q, i) => {
      const startAngle = (i * 90 - 45) * Math.PI / 180;
      const endAngle = ((i + 1) * 90 - 45) * Math.PI / 180;
      const rgb = CORES_DISC[q.trait].rgb;
      
      // Desenhar setor
      pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
      const path = [];
      path.push([centerX, centerY]);
      for (let a = startAngle; a <= endAngle; a += 0.1) {
        path.push([centerX + radius * Math.cos(a), centerY + radius * Math.sin(a)]);
      }
      // Simplificado: desenhar como círculo colorido
      pdf.circle(centerX + (radius/2) * Math.cos((startAngle + endAngle)/2), 
                 centerY + (radius/2) * Math.sin((startAngle + endAngle)/2), 
                 radius/3, 'F');
    });
  };
  
  // ==========================================
  // PÁGINA 1: CAPA
  // ==========================================
  if (onProgress) onProgress(3);
  
  // Background gradiente
  pdf.setFillColor(79, 70, 229);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Elementos decorativos
  pdf.setFillColor(99, 102, 241);
  pdf.circle(pageWidth - 20, 40, 50, 'F');
  pdf.setFillColor(129, 140, 248);
  pdf.circle(30, pageHeight - 50, 35, 'F');
  pdf.setFillColor(67, 56, 202);
  pdf.circle(-20, 150, 40, 'F');
  
  // Título principal
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.text('G2 BEHAVIORAL', pageWidth/2, 60, { align: 'center' });
  pdf.setFontSize(28);
  pdf.text('PROFILE', pageWidth/2, 75, { align: 'center' });
  
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Relatório Completo de Avaliação Comportamental', pageWidth/2, 95, { align: 'center' });
  
  // Box de informações do avaliado
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(margin + 20, 115, contentWidth - 40, 100, 5, 5, 'F');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.nome.toUpperCase(), pageWidth/2, 140, { align: 'center' });
  
  pdf.setTextColor(100, 116, 139);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  if (data.cargo) {
    pdf.text(data.cargo, pageWidth/2, 155, { align: 'center' });
  }
  pdf.text(`Data da Avaliação: ${data.dataAvaliacao}`, pageWidth/2, 170, { align: 'center' });
  
  // Perfil identificado
  const rgbDom = CORES_DISC[dominant].rgb;
  pdf.setFillColor(rgbDom[0], rgbDom[1], rgbDom[2]);
  pdf.roundedRect(margin + 40, 180, contentWidth - 80, 25, 3, 3, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Perfil: ${estiloInfo.nome}`, pageWidth/2, 196, { align: 'center' });
  
  // Alerta de inconsistência se aplicável
  if (data.inconsistente) {
    pdf.setFillColor(251, 191, 36);
    pdf.roundedRect(margin + 25, 230, contentWidth - 50, 20, 3, 3, 'F');
    pdf.setTextColor(120, 53, 15);
    pdf.setFontSize(10);
    pdf.text('ATENÇÃO: Perfil sob análise - Padrões atípicos detectados', pageWidth/2, 243, { align: 'center' });
  }
  
  // Branding
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('G2eGente', pageWidth/2, pageHeight - 50, { align: 'center' });
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Plataforma de Recrutamento Ético', pageWidth/2, pageHeight - 38, { align: 'center' });
  pdf.setFontSize(9);
  pdf.text(`Algoritmo DISC Ipsativo v${data.versao || '2.0'}`, pageWidth/2, pageHeight - 28, { align: 'center' });
  
  // ==========================================
  // PÁGINA 2: ÍNDICE
  // ==========================================
  addNewPage();
  let y = addHeader('ÍNDICE', false);
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ÍNDICE', margin, y);
  y += 15;
  
  const indice = [
    { titulo: 'Introdução ao DISC', paginas: '4-7' },
    { titulo: 'Modelo DISC - Os Quatro Quadrantes', paginas: '8' },
    { titulo: 'Seu Perfil Natural', paginas: '9' },
    { titulo: 'Seu Perfil Social', paginas: '10' },
    { titulo: 'Seu Perfil Adaptado', paginas: '11' },
    { titulo: 'Perfil e Estilo Predominantes', paginas: '12-13' },
    { titulo: 'Seu Estilo de Ser - Resumo', paginas: '14' },
    { titulo: 'Visão de Mundo', paginas: '15' },
    { titulo: 'Motivadores', paginas: '16' },
    { titulo: 'Hábitos nos Ambientes', paginas: '17' },
    { titulo: 'Características e Intensidades', paginas: '18-19' },
    { titulo: 'Níveis dos Padrões DISC', paginas: '20-24' },
    { titulo: 'Desenvolvimento Através do DISC', paginas: '25' },
    { titulo: 'Padrões DISC - Descritivo Comportamental', paginas: '26' },
    { titulo: 'Pontos de Atenção - Dominância', paginas: '27' },
    { titulo: 'Pontos de Atenção - Influência', paginas: '28' },
    { titulo: 'Pontos de Atenção - Estabilidade', paginas: '29' },
    { titulo: 'Pontos de Atenção - Conformidade', paginas: '30' }
  ];
  
  indice.forEach((item, i) => {
    pdf.setFontSize(11);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    pdf.text(item.titulo, margin + 5, y);
    
    // Linha pontilhada
    pdf.setDrawColor(200, 200, 200);
    const textWidth = pdf.getTextWidth(item.titulo);
    const paginaWidth = pdf.getTextWidth(item.paginas);
    const lineStart = margin + 5 + textWidth + 5;
    const lineEnd = pageWidth - margin - paginaWidth - 5;
    for (let x = lineStart; x < lineEnd; x += 3) {
      pdf.circle(x, y - 1, 0.3, 'F');
    }
    
    pdf.text(item.paginas, pageWidth - margin, y, { align: 'right' });
    y += 10;
  });
  
  addFooter(2);
  
  // ==========================================
  // PÁGINAS 3-6: INTRODUÇÃO AO DISC
  // ==========================================
  
  // Página 3: Introdução parte 1
  addNewPage();
  y = addHeader('INTRODUÇÃO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('INTRODUÇÃO AO DISC', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const intro1 = `Bem-vindo ao seu Relatório G2 Behavioral Profile! Este documento apresenta uma análise detalhada do seu perfil comportamental baseada na metodologia DISC, desenvolvida originalmente por William Moulton Marston.

O DISC é uma ferramenta poderosa para autoconhecimento que permite compreender como você naturalmente tende a se comportar, se comunicar e interagir com outras pessoas e com o ambiente ao seu redor.

É importante ressaltar que o DISC não mede inteligência, valores morais, caráter ou personalidade fixa. Em vez disso, ele revela padrões comportamentais que podem ser desenvolvidos e adaptados conforme suas necessidades e objetivos.`;
  
  const intro1Lines = pdf.splitTextToSize(intro1, contentWidth);
  pdf.text(intro1Lines, margin, y);
  y += intro1Lines.length * 5 + 15;
  
  // Box de lembrete
  pdf.setFillColor(239, 246, 255);
  pdf.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');
  pdf.setDrawColor(59, 130, 246);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(margin, y, contentWidth, 50, 3, 3, 'S');
  
  pdf.setTextColor(30, 64, 175);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Lembre-se de que...', margin + 5, y + 10);
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const lembretes = [
    '• Não existe perfil melhor ou pior - cada estilo tem seus pontos fortes',
    '• Seu comportamento pode variar conforme o contexto e situação',
    '• O autoconhecimento é o primeiro passo para o desenvolvimento pessoal',
    '• Este relatório é uma ferramenta de reflexão, não um diagnóstico definitivo'
  ];
  lembretes.forEach((l, i) => {
    pdf.text(l, margin + 5, y + 20 + (i * 7));
  });
  
  addFooter(3);
  
  // Página 4: Introdução parte 2 - Comportamento vs Personalidade
  addNewPage();
  y = addHeader('INTRODUÇÃO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('COMPORTAMENTO vs PERSONALIDADE', margin, y);
  y += 12;
  
  // Box Comportamento
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(margin, y, contentWidth, 55, 3, 3, 'F');
  pdf.setTextColor(21, 128, 61);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('COMPORTAMENTO', margin + 5, y + 10);
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const comportTexto = `O comportamento é a forma como reagimos externamente às situações. É observável, mensurável e pode ser adaptado conscientemente. Por exemplo, em um congestionamento, podemos escolher reagir com calma ou irritação - essa escolha comportamental pode ser desenvolvida.`;
  const comportLines = pdf.splitTextToSize(comportTexto, contentWidth - 10);
  pdf.text(comportLines, margin + 5, y + 20);
  y += 65;
  
  // Box Personalidade
  pdf.setFillColor(254, 243, 199);
  pdf.roundedRect(margin, y, contentWidth, 55, 3, 3, 'F');
  pdf.setTextColor(180, 83, 9);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PERSONALIDADE', margin + 5, y + 10);
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const persTexto = `A personalidade é um conjunto mais profundo e estável de características psicológicas que definem quem somos. O comportamento é a externalização da personalidade, mas pode ser modificado independentemente dela.`;
  const persLines = pdf.splitTextToSize(persTexto, contentWidth - 10);
  pdf.text(persLines, margin + 5, y + 20);
  y += 70;
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('O DISC avalia comportamentos, não personalidade.', margin, y);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const discBeneficios = `Isso significa que você pode desenvolver e adaptar seus comportamentos para melhorar sua eficácia profissional, relacionamentos interpessoais e satisfação pessoal, sem precisar "mudar quem você é".`;
  const discBenLines = pdf.splitTextToSize(discBeneficios, contentWidth);
  pdf.text(discBenLines, margin, y);
  
  addFooter(4);
  
  // Página 5: História e Fundamentação
  addNewPage();
  y = addHeader('INTRODUÇÃO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('HISTÓRIA E FUNDAMENTAÇÃO DO DISC', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const historia = `O estudo do comportamento humano remonta à Grécia Antiga. Hipócrates (460-370 a.C.) propôs a teoria dos quatro temperamentos: Colérico, Sanguíneo, Fleumático e Melancólico, baseados nos "humores" corporais.

Em 1928, o psicólogo William Moulton Marston publicou "Emotions of Normal People", onde apresentou a teoria DISC. Diferente de outros psicólogos da época que estudavam patologias, Marston focou em pessoas saudáveis e suas interações com o ambiente.

O modelo DISC é baseado em duas dimensões fundamentais:

1. RITMO: Pessoas tendem a ser mais Ativas (rápidas, assertivas) ou Reflexivas (ponderadas, cautelosas)

2. ORIENTAÇÃO: Pessoas tendem a ser mais orientadas para Tarefas ou para Pessoas

A combinação dessas dimensões gera os quatro quadrantes DISC:
• D (Dominância): Ativo + Orientado a Tarefas
• I (Influência): Ativo + Orientado a Pessoas  
• S (Estabilidade): Reflexivo + Orientado a Pessoas
• C (Conformidade): Reflexivo + Orientado a Tarefas`;
  
  const historiaLines = pdf.splitTextToSize(historia, contentWidth);
  pdf.text(historiaLines, margin, y);
  
  addFooter(5);
  
  // Página 6: Os Quatro Quadrantes
  addNewPage();
  y = addHeader('INTRODUÇÃO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('OS QUATRO QUADRANTES DISC', margin, y);
  y += 15;
  
  // Desenhar os 4 quadrantes
  const quadSize = 80;
  const quadStartX = margin;
  const quadStartY = y;
  
  const quadrantes = [
    { trait: 'D', x: quadStartX, y: quadStartY },
    { trait: 'I', x: quadStartX + quadSize + 10, y: quadStartY },
    { trait: 'S', x: quadStartX, y: quadStartY + quadSize + 20 },
    { trait: 'C', x: quadStartX + quadSize + 10, y: quadStartY + quadSize + 20 }
  ];
  
  quadrantes.forEach(q => {
    const rgb = CORES_DISC[q.trait].rgb;
    const perfil = PERFIS_DISC[q.trait];
    
    // Box colorido
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(q.x, q.y, quadSize, quadSize, 3, 3, 'F');
    
    // Letra grande
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text(q.trait, q.x + 10, q.y + 20);
    
    // Nome
    pdf.setFontSize(10);
    pdf.text(perfil.nome, q.x + 10, q.y + 32);
    
    // Características principais
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    perfil.caracteristicas.slice(0, 4).forEach((c, i) => {
      pdf.text(`• ${c}`, q.x + 5, q.y + 45 + (i * 8));
    });
  });
  
  y = quadStartY + (quadSize * 2) + 50;
  
  // Tabela resumo
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Resumo dos Focos Principais:', margin, y);
  y += 8;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const focos = [
    { trait: 'D', foco: 'Tarefas e Resultados - "O quê?"' },
    { trait: 'I', foco: 'Pessoas e Comunicação - "Quem?"' },
    { trait: 'S', foco: 'Harmonia e Processos - "Como?"' },
    { trait: 'C', foco: 'Fatos e Qualidade - "Por quê?"' }
  ];
  focos.forEach(f => {
    const rgb = CORES_DISC[f.trait].rgb;
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.circle(margin + 5, y - 2, 3, 'F');
    pdf.text(`${f.trait}: ${f.foco}`, margin + 12, y);
    y += 8;
  });
  
  addFooter(6);
  
  // Página 7: Benefícios do DISC
  addNewPage();
  y = addHeader('INTRODUÇÃO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('BENEFÍCIOS DA AVALIAÇÃO DISC', margin, y);
  y += 15;
  
  const beneficios = [
    { titulo: 'Autoconhecimento', desc: 'Compreenda seus padrões naturais de comportamento e como você responde a diferentes situações.' },
    { titulo: 'Comunicação', desc: 'Aprenda a adaptar sua comunicação para se conectar melhor com pessoas de diferentes estilos.' },
    { titulo: 'Relacionamentos', desc: 'Melhore seus relacionamentos pessoais e profissionais através da compreensão das diferenças.' },
    { titulo: 'Liderança', desc: 'Desenvolva habilidades de liderança adaptando seu estilo às necessidades da equipe.' },
    { titulo: 'Gestão de Conflitos', desc: 'Identifique fontes potenciais de conflito e desenvolva estratégias de resolução.' },
    { titulo: 'Desenvolvimento', desc: 'Identifique áreas de desenvolvimento e crie planos de ação para seu crescimento.' }
  ];
  
  beneficios.forEach((b, i) => {
    pdf.setFillColor(249, 250, 251);
    pdf.roundedRect(margin, y, contentWidth, 28, 3, 3, 'F');
    
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${i + 1}. ${b.titulo}`, margin + 5, y + 10);
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    const descLines = pdf.splitTextToSize(b.desc, contentWidth - 15);
    pdf.text(descLines, margin + 5, y + 18);
    
    y += 33;
  });
  
  // Nota importante
  y += 5;
  pdf.setFillColor(254, 226, 226);
  pdf.roundedRect(margin, y, contentWidth, 25, 3, 3, 'F');
  pdf.setTextColor(153, 27, 27);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('O DISC NÃO mede:', margin + 5, y + 10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Inteligência • Valores morais • Caráter • Honestidade • Competências técnicas', margin + 5, y + 18);
  
  addFooter(7);
  
  // ==========================================
  // PÁGINAS 8-10: PERFIS (Natural, Social, Adaptado)
  // ==========================================
  
  // Página 8: Perfil Natural
  addNewPage();
  y = addHeader('SEU ESTILO DISC');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PERFIL NATURAL', margin, y);
  y += 10;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const perfilNaturalTexto = `O Perfil Natural representa sua essência comportamental - como você naturalmente tende a agir quando não está sob pressão externa ou tentando se adaptar a uma situação específica. Este é o seu "eu autêntico" em termos comportamentais.`;
  const pnLines = pdf.splitTextToSize(perfilNaturalTexto, contentWidth);
  pdf.text(pnLines, margin, y);
  y += pnLines.length * 5 + 15;
  
  // Gráfico do Perfil MAIS (usado como proxy para Natural)
  drawBarChart(margin, y, contentWidth, 70, percentuaisMais, true, 'Gráfico do Perfil Natural (MAIS)');
  y += 95;
  
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  const notaNatural = `Nota: O gráfico "MAIS" representa as características com as quais você mais se identifica, indicando seu perfil comportamental natural - como você se vê e se comporta espontaneamente.`;
  const nnLines = pdf.splitTextToSize(notaNatural, contentWidth);
  pdf.text(nnLines, margin, y);
  
  addFooter(8);
  
  // Página 9: Perfil Social
  addNewPage();
  y = addHeader('SEU ESTILO DISC');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PERFIL SOCIAL', margin, y);
  y += 10;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const perfilSocialTexto = `O Perfil Social representa como você deseja ser percebido pelos outros. É a imagem que você conscientemente projeta em situações sociais e profissionais. Este perfil reflete sua necessidade de aceitação social e como você se adapta às expectativas externas.`;
  const psLines = pdf.splitTextToSize(perfilSocialTexto, contentWidth);
  pdf.text(psLines, margin, y);
  y += psLines.length * 5 + 15;
  
  // Comparação MAIS vs MENOS
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(79, 70, 229);
  pdf.text('Comparação: Perfil Natural vs. Como você quer ser visto', margin, y);
  y += 15;
  
  // Dois gráficos lado a lado
  const halfWidth = (contentWidth - 10) / 2;
  drawBarChart(margin, y, halfWidth, 50, percentuaisMais, true, 'MAIS (Natural)');
  drawBarChart(margin + halfWidth + 10, y, halfWidth, 50, percentuaisMenos, true, 'MENOS (Evita)');
  y += 80;
  
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  const notaSocial = `A diferença entre os gráficos indica o quanto você adapta seu comportamento em situações sociais. Grandes diferenças podem indicar esforço de adaptação significativo.`;
  const nsLines = pdf.splitTextToSize(notaSocial, contentWidth);
  pdf.text(nsLines, margin, y);
  
  addFooter(9);
  
  // Página 10: Perfil Adaptado (Síntese)
  addNewPage();
  y = addHeader('SEU ESTILO DISC');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PERFIL ADAPTADO (SÍNTESE)', margin, y);
  y += 10;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const perfilAdaptadoTexto = `O Perfil Adaptado é calculado através da fórmula MAIS - MENOS e representa seu comportamento atual resultante - a síntese entre o que você naturalmente é e as adaptações que faz. Este é o perfil que mais reflete como você se comporta no dia a dia.`;
  const paLines = pdf.splitTextToSize(perfilAdaptadoTexto, contentWidth);
  pdf.text(paLines, margin, y);
  y += paLines.length * 5 + 15;
  
  // Gráfico principal da Síntese
  drawBarChart(margin, y, contentWidth, 80, percentuaisSintese, true, 'Gráfico do Perfil Adaptado (Síntese)');
  y += 110;
  
  // Estilo identificado
  const rgbEst = CORES_DISC[dominant].rgb;
  pdf.setFillColor(rgbEst[0], rgbEst[1], rgbEst[2]);
  pdf.roundedRect(margin, y, contentWidth, 30, 3, 3, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ESTILO IDENTIFICADO:', margin + 10, y + 12);
  pdf.setFontSize(14);
  pdf.text(estiloInfo.nome.toUpperCase(), margin + 10, y + 24);
  
  addFooter(10);
  
  // ==========================================
  // PÁGINAS 11-12: PERFIL E ESTILO PREDOMINANTES
  // ==========================================
  
  // Página 11
  addNewPage();
  y = addHeader('PERFIL E ESTILO PREDOMINANTES');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${dominant} = ${perfilDominante.nomeCompleto}`, margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  // Usar descrição completa se disponível
  const descricaoUsar = perfilDominante.descricaoCompleta || perfilDominante.descricao;
  const predominanteTexto = `Com base em suas respostas, seu padrão predominante é ${perfilDominante.nomeCompleto} (${dominant}), com uma pontuação de ${percentuaisSintese[dominant]}% no gráfico síntese.

${descricaoUsar}`;
  const predLines = pdf.splitTextToSize(predominanteTexto, contentWidth);
  pdf.text(predLines, margin, y);
  y += predLines.length * 5 + 10;
  
  // Estilo de liderança se disponível
  if (perfilDominante.estiloLideranca) {
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('SEU ESTILO DE LIDERANÇA:', margin, y);
    y += 8;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    const liderLines = pdf.splitTextToSize(perfilDominante.estiloLideranca, contentWidth);
    pdf.text(liderLines, margin + 5, y);
    y += liderLines.length * 4 + 10;
  }
  
  // Perfil secundário se aplicável
  if (estilo.length > 1) {
    const perfilSecundario = PERFIS_DISC[secondary];
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Influência Secundária: ${secondary} = ${perfilSecundario.nome}`, margin, y);
    y += 10;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Usar descrição completa do estilo combinado
    const estiloDesc = estiloInfo.descricaoCompleta || estiloInfo.descricao;
    const secTexto = `Seu estilo combinado ${estilo} (${estiloInfo.nome}) indica que você combina características de ${perfilDominante.nome} e ${perfilSecundario.nome}.

${estiloDesc}`;
    const secLines = pdf.splitTextToSize(secTexto, contentWidth);
    pdf.text(secLines, margin, y);
  }
  
  addFooter(11);
  
  // Página 12: Continuação do Estilo Predominante
  addNewPage();
  y = addHeader('PERFIL E ESTILO PREDOMINANTES');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('CARACTERÍSTICAS DO SEU ESTILO', margin, y);
  y += 15;
  
  // Motivadores
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(21, 128, 61);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('MOTIVADORES', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  pdf.text(perfilDominante.motivadores.join(' • '), margin + 5, y);
  y += 15;
  
  // Medos
  pdf.setFillColor(254, 226, 226);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(153, 27, 27);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('MEDOS BÁSICOS', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  pdf.text(perfilDominante.medos.join(' • '), margin + 5, y);
  y += 15;
  
  // Ambiente Ideal
  pdf.setFillColor(239, 246, 255);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(30, 64, 175);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('AMBIENTE IDEAL', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  const ambLines = pdf.splitTextToSize(perfilDominante.ambienteIdeal, contentWidth - 10);
  pdf.text(ambLines, margin + 5, y);
  y += ambLines.length * 5 + 15;
  
  // Comunicação
  pdf.setFillColor(254, 243, 199);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(180, 83, 9);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('COMO SE COMUNICAR COM VOCÊ', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  const comLines = pdf.splitTextToSize(perfilDominante.comunicacao, contentWidth - 10);
  pdf.text(comLines, margin + 5, y);
  y += comLines.length * 5 + 15;
  
  // Frases Representativas (se disponível)
  if (perfilDominante.frasesRepresentativas && perfilDominante.frasesRepresentativas.length > 0) {
    pdf.setFillColor(249, 250, 251);
    pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('FRASES QUE VOCÊ PROVAVELMENTE DIZ', margin + 5, y + 6);
    y += 15;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'italic');
    perfilDominante.frasesRepresentativas.slice(0, 4).forEach((frase, i) => {
      pdf.text(frase, margin + 10, y);
      y += 7;
    });
  }
  
  addFooter(12);
  
  // ==========================================
  // PÁGINAS 13-16: SEU ESTILO DE SER
  // ==========================================
  
  // Página 13: Resumo do Estilo
  addNewPage();
  y = addHeader('SEU ESTILO DE SER');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`RESUMO DO ESTILO ${estiloInfo.nome.toUpperCase()}`, margin, y);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  // Usar descrição completa do estilo se disponível
  const estiloDescUsar = estiloInfo.descricaoCompleta || estiloInfo.descricao;
  const estiloResumo = pdf.splitTextToSize(estiloDescUsar, contentWidth);
  pdf.text(estiloResumo, margin, y);
  y += estiloResumo.length * 5 + 15;
  
  // Pontos chave do estilo se disponível
  if (estiloInfo.pontosChave && estiloInfo.pontosChave.length > 0) {
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PONTOS-CHAVE DO SEU ESTILO:', margin, y);
    y += 10;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    estiloInfo.pontosChave.forEach(ponto => {
      pdf.text(`• ${ponto}`, margin + 5, y);
      y += 7;
    });
    y += 10;
  }
  
  // Desafios do estilo se disponível
  if (estiloInfo.desafios && estiloInfo.desafios.length > 0) {
    pdf.setTextColor(180, 83, 9);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DESAFIOS A CONSIDERAR:', margin, y);
    y += 10;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    estiloInfo.desafios.forEach(desafio => {
      pdf.text(`• ${desafio}`, margin + 5, y);
      y += 7;
    });
    y += 10;
  }
  
  // Tabela de resumo do estilo
  const estiloItens = [
    { label: 'Características', valor: perfilDominante.caracteristicas.slice(0, 4).join(', ') },
    { label: 'Objetivo Principal', valor: perfilDominante.motivadores[0] },
    { label: 'Emoções Dominantes', valor: `Busca ${perfilDominante.motivadores.slice(0, 2).join(' e ')}` },
    { label: 'Sob Pressão', valor: perfilDominante.pontosAtencao[0] },
    { label: 'Potencializadores', valor: perfilDominante.pontosFortes.slice(0, 2).join(', ') },
    { label: 'Medo Principal', valor: perfilDominante.medos[0] }
  ];
  
  estiloItens.forEach(item => {
    pdf.setFillColor(249, 250, 251);
    pdf.roundedRect(margin, y, contentWidth, 18, 2, 2, 'F');
    
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(item.label.toUpperCase(), margin + 5, y + 7);
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    const valorLines = pdf.splitTextToSize(item.valor, contentWidth - 15);
    pdf.text(valorLines, margin + 5, y + 14);
    
    y += 22;
  });
  
  addFooter(13);
  
  // Página 14: Visão de Mundo
  addNewPage();
  y = addHeader('SEU ESTILO DE SER');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('VISÃO DE MUNDO', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const visaoMundo = `Como pessoa com perfil predominante ${perfilDominante.nome}, você tende a ver o mundo através de uma lente específica:

${perfilDominante.descricaoComportamental}

Sua forma de perceber a vida pessoal e profissional:
${perfilDominante.ambienteIdeal}

Você geralmente se sente mais confortável quando:
${perfilDominante.motivadores.map(m => `• ${m}`).join('\n')}

E pode sentir desconforto quando enfrenta:
${perfilDominante.medos.map(m => `• ${m}`).join('\n')}`;
  
  const visaoLines = pdf.splitTextToSize(visaoMundo, contentWidth);
  pdf.text(visaoLines, margin, y);
  
  addFooter(14);
  
  // Página 15: Motivadores
  addNewPage();
  y = addHeader('SEU ESTILO DE SER');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('MOTIVADORES NA VIDA PESSOAL E PROFISSIONAL', margin, y);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const motivadoresTexto = `Compreender o que motiva você é essencial para seu desenvolvimento e satisfação. Com base em seu perfil ${estiloInfo.nome}, estes são seus principais motivadores:`;
  const motLines = pdf.splitTextToSize(motivadoresTexto, contentWidth);
  pdf.text(motLines, margin, y);
  y += motLines.length * 5 + 15;
  
  perfilDominante.motivadores.forEach((mot, i) => {
    const rgb = CORES_DISC[dominant].rgb;
    // Fundo claro usando valores RGB ajustados
    pdf.setFillColor(Math.min(rgb[0] + 180, 255), Math.min(rgb[1] + 180, 255), Math.min(rgb[2] + 180, 255));
    pdf.roundedRect(margin, y, contentWidth, 20, 3, 3, 'F');
    
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.circle(margin + 10, y + 10, 5, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${i + 1}`, margin + 10, y + 13, { align: 'center' });
    
    // Texto em cor escura para contraste
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(11);
    pdf.text(mot, margin + 25, y + 13);
    
    y += 25;
  });
  
  addFooter(15);
  
  // Página 16: Hábitos nos Ambientes
  addNewPage();
  y = addHeader('SEU ESTILO DE SER');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('HÁBITOS NOS AMBIENTES PESSOAL E PROFISSIONAL', margin, y);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  // Descrição comportamental
  pdf.setFillColor(249, 250, 251);
  pdf.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('DESCRIÇÃO COMPORTAMENTAL', margin + 5, y + 10);
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  const descCompLines = pdf.splitTextToSize(perfilDominante.descricaoComportamental, contentWidth - 10);
  pdf.text(descCompLines, margin + 5, y + 20);
  y += 60;
  
  // Descrição no ambiente
  pdf.setFillColor(239, 246, 255);
  pdf.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');
  pdf.setTextColor(30, 64, 175);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('NO AMBIENTE DE TRABALHO', margin + 5, y + 10);
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  const descAmbLines = pdf.splitTextToSize(perfilDominante.descricaoAmbiente, contentWidth - 10);
  pdf.text(descAmbLines, margin + 5, y + 20);
  y += 60;
  
  // Contribuição para equipe
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(margin, y, contentWidth, 40, 3, 3, 'F');
  pdf.setTextColor(21, 128, 61);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('CONTRIBUIÇÃO PARA A EQUIPE', margin + 5, y + 10);
  pdf.setTextColor(50, 50, 50);
  pdf.setFont('helvetica', 'normal');
  const contribLines = pdf.splitTextToSize(perfilDominante.contribuicaoEquipe, contentWidth - 10);
  pdf.text(contribLines, margin + 5, y + 20);
  y += 50;
  
  // Comportamento sob pressão (se disponível)
  if (perfilDominante.sobPressao) {
    pdf.setFillColor(254, 226, 226);
    pdf.roundedRect(margin, y, contentWidth, 40, 3, 3, 'F');
    pdf.setTextColor(153, 27, 27);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('COMPORTAMENTO SOB PRESSÃO', margin + 5, y + 10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    const pressaoLines = pdf.splitTextToSize(perfilDominante.sobPressao, contentWidth - 10);
    pdf.text(pressaoLines, margin + 5, y + 20);
  }
  
  addFooter(16);
  
  // Página adicional: Carreiras Indicadas
  addNewPage();
  y = addHeader('SEU ESTILO DE SER');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('CARREIRAS E ÁREAS INDICADAS', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const carreirasIntro = `Com base em seu perfil ${estiloInfo.nome}, você naturalmente se adapta bem a funções e ambientes que valorizam suas características principais. Abaixo estão algumas áreas e carreiras que podem ser especialmente adequadas para você:`;
  const carrIntroLines = pdf.splitTextToSize(carreirasIntro, contentWidth);
  pdf.text(carrIntroLines, margin, y);
  y += carrIntroLines.length * 5 + 15;
  
  // Carreiras indicadas
  if (perfilDominante.carreirasIndicadas && perfilDominante.carreirasIndicadas.length > 0) {
    const rgbCarr = CORES_DISC[dominant].rgb;
    pdf.setFillColor(rgbCarr[0], rgbCarr[1], rgbCarr[2]);
    pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CARREIRAS RECOMENDADAS', margin + 5, y + 6);
    y += 15;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Grid de carreiras
    const carreiras = perfilDominante.carreirasIndicadas;
    const cols = 2;
    const colWidth = (contentWidth - 10) / cols;
    
    carreiras.forEach((carreira, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = margin + 5 + (col * colWidth);
      const yPos = y + (row * 10);
      
      pdf.setFillColor(rgbCarr[0], rgbCarr[1], rgbCarr[2]);
      pdf.circle(x + 3, yPos - 2, 2, 'F');
      pdf.setTextColor(50, 50, 50);
      pdf.text(carreira, x + 10, yPos);
    });
    
    y += Math.ceil(carreiras.length / cols) * 10 + 15;
  }
  
  // Como lidar com este perfil
  if (perfilDominante.comoLidar && perfilDominante.comoLidar.length > 0) {
    pdf.setFillColor(239, 246, 255);
    pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    pdf.setTextColor(30, 64, 175);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('COMO OUTROS DEVEM INTERAGIR COM VOCÊ', margin + 5, y + 6);
    y += 15;
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    perfilDominante.comoLidar.forEach((dica) => {
      pdf.text(`• ${dica}`, margin + 5, y);
      y += 8;
    });
  }
  
  addFooter(17);
  
  // ==========================================
  // PÁGINAS 17-18: CARACTERÍSTICAS E INTENSIDADES
  // ==========================================
  
  // Página 17
  addNewPage();
  y = addHeader('CARACTERÍSTICAS E INTENSIDADES');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('TABELA DE CARACTERÍSTICAS DISC', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Esta tabela apresenta as características associadas a cada nível dos padrões DISC.', margin, y);
  y += 15;
  
  // Tabela para D e I
  ['D', 'I'].forEach(trait => {
    const rgb = CORES_DISC[trait].rgb;
    const caracs = CARACTERISTICAS_INTENSIDADE[trait];
    
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${trait} - ${PERFIS_DISC[trait].nome}`, margin + 5, y + 6);
    y += 12;
    
    // Alto
    pdf.setTextColor(21, 128, 61);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ALTO:', margin + 5, y);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    const altoText = caracs.alto.join(', ');
    const altoLines = pdf.splitTextToSize(altoText, contentWidth - 25);
    pdf.text(altoLines, margin + 25, y);
    y += altoLines.length * 4 + 5;
    
    // Baixo
    pdf.setTextColor(153, 27, 27);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BAIXO:', margin + 5, y);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    const baixoText = caracs.baixo.join(', ');
    const baixoLines = pdf.splitTextToSize(baixoText, contentWidth - 25);
    pdf.text(baixoLines, margin + 25, y);
    y += baixoLines.length * 4 + 15;
  });
  
  addFooter(17);
  
  // Página 18: Continuação S e C
  addNewPage();
  y = addHeader('CARACTERÍSTICAS E INTENSIDADES');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('TABELA DE CARACTERÍSTICAS DISC (Continuação)', margin, y);
  y += 15;
  
  ['S', 'C'].forEach(trait => {
    const rgb = CORES_DISC[trait].rgb;
    const caracs = CARACTERISTICAS_INTENSIDADE[trait];
    
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${trait} - ${PERFIS_DISC[trait].nome}`, margin + 5, y + 6);
    y += 12;
    
    // Alto
    pdf.setTextColor(21, 128, 61);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ALTO:', margin + 5, y);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    const altoText = caracs.alto.join(', ');
    const altoLines = pdf.splitTextToSize(altoText, contentWidth - 25);
    pdf.text(altoLines, margin + 25, y);
    y += altoLines.length * 4 + 5;
    
    // Baixo
    pdf.setTextColor(153, 27, 27);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BAIXO:', margin + 5, y);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    const baixoText = caracs.baixo.join(', ');
    const baixoLines = pdf.splitTextToSize(baixoText, contentWidth - 25);
    pdf.text(baixoLines, margin + 25, y);
    y += baixoLines.length * 4 + 15;
  });
  
  // Nota sobre uso
  y += 10;
  pdf.setFillColor(254, 243, 199);
  pdf.roundedRect(margin, y, contentWidth, 35, 3, 3, 'F');
  pdf.setTextColor(180, 83, 9);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Como usar esta tabela:', margin + 5, y + 10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const usoTabela = `Identifique suas características de ALTO (seus pontos fortes naturais) e de BAIXO (áreas onde você pode precisar de mais esforço consciente). Use estas informações para desenvolvimento pessoal.`;
  const usoLines = pdf.splitTextToSize(usoTabela, contentWidth - 10);
  pdf.text(usoLines, margin + 5, y + 18);
  
  addFooter(18);
  
  // ==========================================
  // PÁGINAS 19-23: NÍVEIS DOS PADRÕES D/I/S/C
  // ==========================================
  
  // Página 19: Introdução aos Níveis
  addNewPage();
  y = addHeader('NÍVEIS DOS PADRÕES');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('SEUS NÍVEIS DOS PADRÕES DISC', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const niveisIntro = `As próximas páginas detalham seu nível em cada um dos quatro padrões DISC. Estas características definem sua singularidade e podem ser vistas como pontos fortes ou oportunidades de desenvolvimento.

Sugestão: Marque os adjetivos que mais ressoam com você e utilize-os para potencializar seus pontos fortes. Para os que parecem desafiadores, considere buscar apoio ou desenvolvimento específico.`;
  const nivIntroLines = pdf.splitTextToSize(niveisIntro, contentWidth);
  pdf.text(nivIntroLines, margin, y);
  y += nivIntroLines.length * 5 + 20;
  
  // Gráfico resumo dos 4 padrões
  drawBarChart(margin, y, contentWidth, 80, percentuaisSintese, true, 'Resumo dos Seus Padrões');
  y += 110;
  
  // Tabela de percentuais
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  ['D', 'I', 'S', 'C'].forEach((t, i) => {
    const rgb = CORES_DISC[t].rgb;
    const x = margin + (i * 45);
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(x, y, 40, 25, 3, 3, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.text(t, x + 20, y + 10, { align: 'center' });
    pdf.setFontSize(14);
    pdf.text(`${percentuaisSintese[t]}%`, x + 20, y + 20, { align: 'center' });
    pdf.setFontSize(10);
  });
  
  addFooter(19);
  
  // Páginas 20-23: Detalhes de cada padrão
  ['D', 'I', 'S', 'C'].forEach((trait, idx) => {
    addNewPage();
    y = addHeader('NÍVEIS DOS PADRÕES');
    
    const perfil = PERFIS_DISC[trait];
    const rgb = CORES_DISC[trait].rgb;
    const score = percentuaisSintese[trait];
    
    // Título com cor
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(margin, y - 5, contentWidth, 25, 3, 3, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`NÍVEL DO PADRÃO ${perfil.nomeCompleto}`, margin + 10, y + 5);
    pdf.setFontSize(20);
    pdf.text(`${score}%`, pageWidth - margin - 10, y + 8, { align: 'right' });
    y += 30;
    
    // Descrição do padrão
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const descLines = pdf.splitTextToSize(perfil.descricao, contentWidth);
    pdf.text(descLines, margin, y);
    y += descLines.length * 5 + 10;
    
    // Características com base no nível
    const nivel = score >= 70 ? 'alto' : score <= 30 ? 'baixo' : 'medio';
    const nivelLabel = nivel === 'alto' ? 'CARACTERÍSTICAS DE ALTO ' + trait : 
                       nivel === 'baixo' ? 'CARACTERÍSTICAS DE BAIXO ' + trait : 
                       'CARACTERÍSTICAS EQUILIBRADAS';
    
    pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(nivelLabel, margin, y);
    y += 10;
    
    const caracsNivel = nivel === 'alto' ? CARACTERISTICAS_INTENSIDADE[trait].alto :
                        nivel === 'baixo' ? CARACTERISTICAS_INTENSIDADE[trait].baixo :
                        [...CARACTERISTICAS_INTENSIDADE[trait].alto.slice(0, 3), ...CARACTERISTICAS_INTENSIDADE[trait].baixo.slice(0, 3)];
    
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    caracsNivel.forEach(c => {
      pdf.text(`• ${c}`, margin + 5, y);
      y += 7;
    });
    y += 10;
    
    // Pontos fortes e atenção
    const colWidth = (contentWidth - 10) / 2;
    
    // Pontos fortes
    pdf.setFillColor(220, 252, 231);
    pdf.roundedRect(margin, y, colWidth, 60, 3, 3, 'F');
    pdf.setTextColor(21, 128, 61);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PONTOS FORTES', margin + 5, y + 10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    let pfY = y + 20;
    perfil.pontosFortes.slice(0, 4).forEach((pf, i) => {
      const pfText = pdf.splitTextToSize(`✓ ${pf}`, colWidth - 10);
      pdf.text(pfText[0], margin + 5, pfY);
      pfY += 10;
    });
    
    // Pontos de atenção
    pdf.setFillColor(254, 226, 226);
    pdf.roundedRect(margin + colWidth + 10, y, colWidth, 60, 3, 3, 'F');
    pdf.setTextColor(153, 27, 27);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PONTOS DE ATENÇÃO', margin + colWidth + 15, y + 10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    let paY = y + 20;
    perfil.pontosAtencao.slice(0, 4).forEach((pa, i) => {
      const paText = pdf.splitTextToSize(`⚠ ${pa}`, colWidth - 10);
      pdf.text(paText[0], margin + colWidth + 15, paY);
      paY += 10;
    });
    
    addFooter(20 + idx);
  });
  
  // ==========================================
  // PÁGINA 24: DESENVOLVIMENTO ATRAVÉS DO DISC
  // ==========================================
  addNewPage();
  y = addHeader('DESENVOLVIMENTO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('DESENVOLVIMENTO ATRAVÉS DO DISC', margin, y);
  y += 12;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const devIntro = `Compreender como se relacionar com diferentes perfis DISC é essencial para uma comunicação eficaz e relacionamentos produtivos. A tabela abaixo mostra o grau de conforto natural entre diferentes combinações de perfis.`;
  const devIntroLines = pdf.splitTextToSize(devIntro, contentWidth);
  pdf.text(devIntroLines, margin, y);
  y += devIntroLines.length * 5 + 15;
  
  // Tabela de grau de conforto
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('GRAU DE CONFORTO (1-5):', margin, y);
  y += 8;
  pdf.setFont('helvetica', 'normal');
  pdf.text('1 = Muito desafiador | 3 = Neutro | 5 = Muito confortável', margin, y);
  y += 15;
  
  Object.entries(GRAU_CONFORTO).forEach(([combo, info]) => {
    const [t1, t2] = combo.split('-');
    const rgb1 = CORES_DISC[t1].rgb;
    const rgb2 = CORES_DISC[t2].rgb;
    
    // Combo
    pdf.setFillColor(rgb1[0], rgb1[1], rgb1[2]);
    pdf.circle(margin + 5, y, 4, 'F');
    pdf.setFillColor(rgb2[0], rgb2[1], rgb2[2]);
    pdf.circle(margin + 15, y, 4, 'F');
    
    // Grau
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${combo}: ${info.grau}`, margin + 25, y + 3);
    
    // Descrição
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(info.descricao, margin + 55, y + 3);
    
    y += 12;
  });
  
  addFooter(24);
  
  // ==========================================
  // PÁGINA 25: PADRÕES DISC - DESCRITIVO
  // ==========================================
  addNewPage();
  y = addHeader('PADRÕES DISC');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PADRÕES DISC - DESCRITIVO COMPORTAMENTAL', margin, y);
  y += 15;
  
  // Resumo dos 4 padrões puros
  ['D', 'I', 'S', 'C'].forEach(trait => {
    const perfil = PERFIS_DISC[trait];
    const rgb = CORES_DISC[trait].rgb;
    
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.roundedRect(margin, y, contentWidth, 45, 3, 3, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${trait} - ${perfil.nome}`, margin + 5, y + 10);
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Medo: ${perfil.medos[0]}`, margin + 5, y + 20);
    pdf.text(`Motivador: ${perfil.motivadores[0]}`, margin + 5, y + 27);
    pdf.text(`Valor: ${perfil.pontosFortes[0]}`, margin + 5, y + 34);
    pdf.text(`Comunicação: ${perfil.comunicacao.substring(0, 50)}...`, margin + 5, y + 41);
    
    y += 50;
  });
  
  addFooter(25);
  
  // ==========================================
  // PÁGINAS 26-29: PONTOS DE ATENÇÃO POR PERFIL
  // ==========================================
  ['D', 'I', 'S', 'C'].forEach((trait, idx) => {
    addNewPage();
    y = addHeader('DESCRIÇÃO E PONTOS DE ATENÇÃO');
    
    const perfil = PERFIS_DISC[trait];
    const rgb = CORES_DISC[trait].rgb;
    
    pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`ALTO ${trait} - ${perfil.nomeCompleto}`, margin, y);
    y += 15;
    
    // Descrição Comportamental
    pdf.setFillColor(249, 250, 251);
    pdf.roundedRect(margin, y, contentWidth, 55, 3, 3, 'F');
    pdf.setTextColor(79, 70, 229);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DESCRIÇÃO COMPORTAMENTAL', margin + 5, y + 10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    const descCompLines2 = pdf.splitTextToSize(perfil.descricaoComportamental, contentWidth - 10);
    pdf.text(descCompLines2, margin + 5, y + 20);
    y += 65;
    
    // Descrição no Ambiente
    pdf.setFillColor(239, 246, 255);
    pdf.roundedRect(margin, y, contentWidth, 55, 3, 3, 'F');
    pdf.setTextColor(30, 64, 175);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DESCRIÇÃO COMPORTAMENTAL NO AMBIENTE', margin + 5, y + 10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    const descAmbLines2 = pdf.splitTextToSize(perfil.descricaoAmbiente, contentWidth - 10);
    pdf.text(descAmbLines2, margin + 5, y + 20);
    y += 65;
    
    // Pontos de Atenção - Apoio do Coach
    pdf.setFillColor(254, 243, 199);
    pdf.roundedRect(margin, y, contentWidth, 70, 3, 3, 'F');
    pdf.setTextColor(180, 83, 9);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PONTOS DE ATENÇÃO - APOIO DO COACH', margin + 5, y + 10);
    pdf.setTextColor(50, 50, 50);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    let coachY = y + 22;
    perfil.apoioCoach.forEach((apoio, i) => {
      const apoioLines = pdf.splitTextToSize(`⚠ ${apoio}`, contentWidth - 15);
      pdf.text(apoioLines[0], margin + 5, coachY);
      coachY += 10;
    });
    
    addFooter(26 + idx);
  });
  
  // ==========================================
  // PÁGINA 30: CONCLUSÃO E ASSINATURA
  // ==========================================
  addNewPage();
  y = addHeader('CONCLUSÃO');
  
  pdf.setTextColor(79, 70, 229);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('CONCLUSÃO', margin, y);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  const conclusao = `Este relatório G2 Behavioral Profile oferece uma visão abrangente de seu perfil comportamental baseado na metodologia DISC. As informações aqui contidas devem ser usadas como ferramenta de autoconhecimento e desenvolvimento.

Lembre-se de que:
• Não existe perfil melhor ou pior - cada estilo tem seus pontos fortes
• O comportamento pode ser desenvolvido e adaptado
• O autoconhecimento é o primeiro passo para a melhoria contínua
• Busque feedback de pessoas de confiança para validar suas percepções

Recomendamos que você:
1. Revise periodicamente este relatório
2. Identifique 2-3 áreas prioritárias de desenvolvimento
3. Busque apoio de um coach ou mentor se necessário
4. Pratique conscientemente comportamentos que deseja desenvolver`;
  
  const conclusaoLines = pdf.splitTextToSize(conclusao, contentWidth);
  pdf.text(conclusaoLines, margin, y);
  y += conclusaoLines.length * 5 + 30;
  
  // Box de agradecimento
  pdf.setFillColor(79, 70, 229);
  pdf.roundedRect(margin, y, contentWidth, 30, 3, 3, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Obrigado por utilizar o G2 Behavioral Profile!', pageWidth/2, y + 12, { align: 'center' });
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('G2eGente - Plataforma de Recrutamento Ético', pageWidth/2, y + 22, { align: 'center' });
  y += 45;
  
  // Assinatura do psicólogo
  if (psicologo) {
    pdf.setDrawColor(200, 200, 200);
    pdf.line(pageWidth - margin - 70, y, pageWidth - margin, y);
    pdf.setTextColor(50, 50, 50);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(psicologo.nome, pageWidth - margin, y + 8, { align: 'right' });
    if (psicologo.crp) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.text(`CRP: ${psicologo.crp}`, pageWidth - margin, y + 16, { align: 'right' });
    }
  }
  
  // Dados técnicos
  pdf.setFontSize(8);
  pdf.setTextColor(100, 116, 139);
  pdf.text(`Relatório gerado em: ${new Date().toLocaleDateString('pt-BR')}`, margin, pageHeight - 25);
  pdf.text(`Algoritmo: DISC Ipsativo v${data.versao || '2.0'}`, margin, pageHeight - 20);
  pdf.text(`Avaliação realizada em: ${data.dataAvaliacao}`, margin, pageHeight - 15);
  
  addFooter(30);
  
  return pdf;
};

// ==========================================
// COMPONENTE REACT
// ==========================================

export default function RelatorioG2BPCompleto({ 
  open, 
  onClose, 
  invite, 
  psychologists = [],
  crpObrigatorio = false
}) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [psicologoSelecionado, setPsicologoSelecionado] = useState(null);
  const [nomeManual, setNomeManual] = useState('');
  const [crpManual, setCrpManual] = useState('');
  const [usarManual, setUsarManual] = useState(psychologists.length === 0);

  const g2bpResult = invite?.resultado?.g2bp_result;
  
  if (!g2bpResult) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado não disponível</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500">
            O resultado do G2BP não está disponível para este convite.
          </p>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const dominantProfile = PERFIS_DISC[g2bpResult.dominant];
  const sorted = Object.entries(g2bpResult.percentuaisSintese || g2bpResult.percentages || {}).sort(([,a], [,b]) => b - a);
  const estilo = sorted[1]?.[1] >= sorted[0]?.[1] * 0.7 ? sorted[0][0] + sorted[1][0] : sorted[0]?.[0] || 'D';
  const estiloInfo = ESTILOS_COMBINADOS[estilo] || ESTILOS_COMBINADOS[g2bpResult.dominant];

  const dadosRelatorio = {
    nome: invite.nome_destinatario,
    cargo: invite.cargo_funcao || '',
    dataAvaliacao: invite.responded_at 
      ? new Date(invite.responded_at).toLocaleDateString('pt-BR', { 
          day: '2-digit', month: 'long', year: 'numeric' 
        })
      : new Date().toLocaleDateString('pt-BR'),
    dominant: g2bpResult.dominant,
    scoresMais: g2bpResult.scoresMais,
    scoresMenos: g2bpResult.scoresMenos,
    scoresSintese: g2bpResult.scoresSintese,
    percentuaisMais: g2bpResult.percentuaisMais,
    percentuaisMenos: g2bpResult.percentuaisMenos,
    percentuaisSintese: g2bpResult.percentuaisSintese,
    scores: g2bpResult.scores || g2bpResult.scoresSintese,
    percentages: g2bpResult.percentages || g2bpResult.percentuaisSintese,
    estilo: g2bpResult.estilo,
    inconsistente: g2bpResult.inconsistente || false,
    motivoInconsistencia: g2bpResult.motivoInconsistencia || null,
    versao: g2bpResult.versao || '2.0'
  };

  const getPsicologoData = () => {
    if (usarManual) {
      if (!nomeManual.trim()) return null;
      return { nome: nomeManual.trim(), crp: crpManual.trim() || null };
    }
    if (psicologoSelecionado) {
      const psi = psychologists.find(p => p.id === psicologoSelecionado);
      return psi ? { nome: psi.nome, crp: psi.crp } : null;
    }
    return null;
  };

  const canGenerate = () => {
    const psi = getPsicologoData();
    if (!psi) return false;
    if (crpObrigatorio && !psi.crp) return false;
    return true;
  };

  const handleGenerate = async () => {
    const psicologo = getPsicologoData();
    if (!psicologo) {
      toast.error('Informe o responsável pelo relatório');
      return;
    }
    if (crpObrigatorio && !psicologo.crp) {
      toast.error('CRP é obrigatório para este relatório');
      return;
    }

    setLoading(true);
    setProgress(0);
    
    try {
      const pdf = await generateRelatorioCompleto(dadosRelatorio, psicologo, setProgress);
      
      const fileName = `Relatorio_G2BP_Completo_${invite.nome_destinatario.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success('Relatório completo de 30 páginas gerado com sucesso!');
      setProgress(100);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Relatório G2BP Completo (30 páginas)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview dos dados */}
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Dados do Avaliado
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span><strong>Nome:</strong> {invite.nome_destinatario}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <span><strong>Cargo:</strong> {invite.cargo_funcao || 'Não informado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span><strong>Data:</strong> {dadosRelatorio.dataAvaliacao}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-indigo-500" />
                <span><strong>Estilo:</strong></span>
                <Badge className="bg-indigo-100 text-indigo-700">
                  {estiloInfo?.nome || estilo}
                </Badge>
              </div>
            </div>
          </div>

          {/* Conteúdo do relatório */}
          <div className="bg-white rounded-lg p-4 border">
            <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Conteúdo do Relatório Completo
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
              <div>• Introdução ao DISC</div>
              <div>• Os 4 Quadrantes</div>
              <div>• Perfil Natural</div>
              <div>• Perfil Social</div>
              <div>• Perfil Adaptado</div>
              <div>• Estilo Predominante</div>
              <div>• Visão de Mundo</div>
              <div>• Motivadores</div>
              <div>• Hábitos</div>
              <div>• Características</div>
              <div>• Níveis D/I/S/C</div>
              <div>• Desenvolvimento</div>
              <div>• Pontos de Atenção</div>
              <div>• Conclusão</div>
            </div>
          </div>

          {/* Alerta de inconsistência */}
          {dadosRelatorio.inconsistente && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Perfil Sob Análise</p>
                  <p className="text-sm text-amber-700">{dadosRelatorio.motivoInconsistencia}</p>
                </div>
              </div>
            </div>
          )}

          {/* Seleção de Psicólogo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-700">Responsável pelo Relatório</h4>
              {crpObrigatorio && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  CRP Obrigatório
                </Badge>
              )}
            </div>

            {psychologists.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant={!usarManual ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUsarManual(false)}
                >
                  Psicólogo Cadastrado
                </Button>
                <Button
                  variant={usarManual ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUsarManual(true)}
                >
                  Informar Manualmente
                </Button>
              </div>
            )}

            {!usarManual && psychologists.length > 0 ? (
              <div className="space-y-2">
                <Label>Selecione o Psicólogo</Label>
                <select
                  value={psicologoSelecionado || ''}
                  onChange={(e) => setPsicologoSelecionado(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Selecione...</option>
                  {psychologists.map((psi) => (
                    <option key={psi.id} value={psi.id}>
                      {psi.nome} - CRP: {psi.crp}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Responsável *</Label>
                  <Input
                    value={nomeManual}
                    onChange={(e) => setNomeManual(e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CRP {crpObrigatorio && '*'}</Label>
                  <Input
                    value={crpManual}
                    onChange={(e) => setCrpManual(e.target.value.toUpperCase())}
                    placeholder="Ex: 06/123456"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Progress bar */}
          {loading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Gerando relatório...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleGenerate}
            disabled={loading || !canGenerate()}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando PDF...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Gerar PDF (30 páginas)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
